import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Button } from "../ui/button";
import { AsyncButton } from "../ui/async-button";
import { Card } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { PanelHeader } from "./PanelHeader";
import {
  Select as UiSelect,
  SelectContent as UiSelectContent,
  SelectItem as UiSelectItem,
  SelectTrigger as UiSelectTrigger,
  SelectValue as UiSelectValue,
} from "../ui/select";
import type { Book } from "../../types";
import {
  estimateTotalPages,
  PLATFORM_PRESETS,
  TRIM_SIZE_LABELS,
  BODY_FONT_LABELS,
  PUBLISH_FONT_LABELS,
  LINE_SPACING_OPTIONS,
  CHAPTER_DECORATION_ORNAMENTS,
  CHAPTER_DECORATION_LINE_STYLES,
  defaultChapterDecoration,
} from "../../../shared/publishFormattingShared";
import { userFacingError } from "../../../shared/userFacingError";
import {
  formatTemplateToPublishSettings,
  migrateTemplate,
} from "../../../shared/templateSchema";
import type {
  PublishSettings,
  TrimSize,
  BodyFont,
  PublishFont,
  ChapterHeadingStyle,
  ChapterNumberFormat,
  ChapterStartPage,
  PageNumberPlacement,
  HeaderContent,
  TocStyle,
  PublishPlatform,
  CaptionPlacement,
  CaptionAlign,
  HeadingAlign,
  ChapterDecoration,
  ChapterDecorationKind,
  ChapterDecorationLineStyle,
} from "../../../shared/publishFormattingShared";
import type { PageModel } from "../../../shared/pageModel";
import { useCoverLayout } from "../../hooks/useCoverLayout";
import type { BookCoverLayout } from "../../../shared/coverGeometry";
import type { PreflightReport } from "../../../shared/preflight";
import { runPreflight } from "../../../shared/preflight";
import {
  showPublisherPackaging,
  personaFromBookMetadata,
} from "../../../shared/personaMode";
import { WysiwygSpreadPreview } from "./WysiwygSpreadPreview";
import { CoverEditor } from "./CoverEditor";
import { PublishThemeStrip, PublishWizard } from "./PublishWizard";
import { applyPublishTheme } from "../../../shared/publishThemes";
import { assessPublishReadiness } from "../../../shared/publishReadiness";
import { cn } from "../../lib/utils";

type Props = {
  book: Book;
  authToken?: string;
  onBookChange: (updates: Partial<Book>) => void;
  onRequestTranslate?: () => void;
};

function getInitialSettings(book: Book): PublishSettings {
  const meta = book.metadata as Record<string, unknown>;
  const saved = meta?.publishSettings ?? meta?.formatTemplate;
  return formatTemplateToPublishSettings(migrateTemplate(saved));
}

/** Write publish settings into book metadata (caller merges onto current metadata). */
function persistSettingsPatch(
  next: PublishSettings,
  onBookChange: (updates: Partial<Book>) => void
): void {
  onBookChange({
    metadata: {
      publishSettings: next,
      formatTemplate: migrateTemplate(next),
    },
  });
}

/** Rasterize SVG (or keep raster) to a PNG data URL for PDFKit. */
function fileToDecorationImage(file: File): Promise<{ dataUrl: string; aspect: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read image file."));
    reader.onload = () => {
      const raw = typeof reader.result === "string" ? reader.result : "";
      if (!raw) {
        reject(new Error("Empty image data."));
        return;
      }
      const img = new Image();
      img.onload = () => {
        const w = Math.max(1, img.naturalWidth || img.width);
        const h = Math.max(1, img.naturalHeight || img.height);
        const maxEdge = 1200;
        const scale = Math.min(1, maxEdge / Math.max(w, h));
        const cw = Math.max(1, Math.round(w * scale));
        const ch = Math.max(1, Math.round(h * scale));
        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas unavailable."));
          return;
        }
        ctx.clearRect(0, 0, cw, ch);
        ctx.drawImage(img, 0, 0, cw, ch);
        resolve({ dataUrl: canvas.toDataURL("image/png"), aspect: w / h });
      };
      img.onerror = () => reject(new Error("Could not decode image."));
      img.src = raw;
    };
    reader.readAsDataURL(file);
  });
}

function Section({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card size="sm" className="gap-0 rounded-none border-x-0 border-t-0 bg-transparent py-0 ring-0">
      <Button
        type="button"
        variant="ghost"
        className="h-auto w-full justify-between rounded-none px-0 py-2.5 text-left"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-[0.72rem] font-bold uppercase tracking-[0.07em] text-muted-foreground">{title}</span>
        <span className="text-xs text-muted-foreground" aria-hidden="true">{open ? "▾" : "▸"}</span>
      </Button>
      {open && <div className="flex flex-col gap-2 pb-3.5">{children}</div>}
    </Card>
  );
}

type SettingsTabId = "page" | "themes" | "type" | "chapters" | "matter" | "headers" | "preflight";
type PreviewTabId = "spread" | "cover";

const SETTINGS_TABS: { id: SettingsTabId; label: string }[] = [
  { id: "page", label: "Page" },
  { id: "themes", label: "Themes" },
  { id: "type", label: "Type" },
  { id: "chapters", label: "Chapters" },
  { id: "matter", label: "Matter" },
  { id: "headers", label: "Headers" },
  { id: "preflight", label: "Check" },
];

const PREVIEW_TABS: { id: PreviewTabId; label: string }[] = [
  { id: "spread", label: "Preview" },
  { id: "cover", label: "Cover" },
];

function TabBar<T extends string>({
  tabs,
  value,
  onChange,
  ariaLabel,
  panelId,
}: {
  tabs: { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
  ariaLabel: string;
  panelId: string;
}) {
  return (
    <Tabs value={value} onValueChange={(next) => onChange(next as T)} className="shrink-0">
      <TabsList
        variant="line"
        className="border-b border-border px-1"
        aria-label={ariaLabel}
      >
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.id}
            value={tab.id}
            variant="line"
            id={`${panelId}-tab-${tab.id}`}
            aria-controls={panelId}
            className="h-8 px-2 text-xs"
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-30 shrink-0 text-[0.82rem] text-foreground">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

function Toggle({
  value,
  onChange,
  label,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  label?: string;
}) {
  return (
    <label className="flex cursor-pointer select-none items-center gap-1.5 text-[0.82rem] text-foreground">
      <Checkbox
        checked={value}
        onCheckedChange={(checked) => onChange(checked === true)}
      />
      {label && <span>{label}</span>}
    </label>
  );
}

function Select<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <UiSelect value={value} onValueChange={(v) => onChange(v as T)}>
      <UiSelectTrigger className="h-8 w-full min-w-30 flex-1" size="sm">
        <UiSelectValue />
      </UiSelectTrigger>
      <UiSelectContent>
        {options.map((o) => (
          <UiSelectItem key={o.value} value={o.value}>
            {o.label}
          </UiSelectItem>
        ))}
      </UiSelectContent>
    </UiSelect>
  );
}

function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 0.01,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
}) {
  return (
    <Input
      type="number"
      className="w-17.5 text-right text-[0.82rem]"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e) => {
        const v = parseFloat(e.target.value);
        if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
      }}
    />
  );
}

function PageDiagram({ settings }: { settings: PublishSettings }) {
  const w = 80;
  const h = 110;
  const { top, bottom, inner, outer } = settings.margins;
  const scale = Math.min(w / 6.5, h / 10);
  const tPx = top * scale;
  const bPx = bottom * scale;
  const iPx = inner * scale;
  const oPx = outer * scale;

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className="mt-1 shrink-0 self-center"
    >
      <rect
        x={2}
        y={2}
        width={w - 4}
        height={h - 4}
        className="fill-card stroke-border"
        strokeWidth={0.5}
      />
      <rect
        x={2 + iPx}
        y={2 + tPx}
        width={w - 4 - iPx - oPx}
        height={h - 4 - tPx - bPx}
        className="fill-muted stroke-border"
        strokeWidth={0.5}
        strokeDasharray="2 1"
      />
      <text x={w / 2} y={2 + tPx / 2 + 3} textAnchor="middle" fontSize={5} className="fill-muted-foreground">
        {top}"
      </text>
      <text x={w / 2} y={h - 2 - bPx / 2 + 3} textAnchor="middle" fontSize={5} className="fill-muted-foreground">
        {bottom}"
      </text>
      <text
        x={2 + iPx / 2}
        y={h / 2}
        textAnchor="middle"
        fontSize={4}
        className="fill-muted-foreground"
        transform={`rotate(-90, ${2 + iPx / 2}, ${h / 2})`}
      >
        {inner}"
      </text>
      <text
        x={w - 2 - oPx / 2}
        y={h / 2}
        textAnchor="middle"
        fontSize={4}
        className="fill-muted-foreground"
        transform={`rotate(90, ${w - 2 - oPx / 2}, ${h / 2})`}
      >
        {outer}"
      </text>
    </svg>
  );
}

export function PublishPanel({ book, authToken, onBookChange, onRequestTranslate }: Props) {
  const [settings, setSettings] = useState<PublishSettings>(() => getInitialSettings(book));
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState<{ text: string; type: "ok" | "error" } | null>(null);
  const [previewPages, setPreviewPages] = useState<PageModel[]>([]);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [coverExporting, setCoverExporting] = useState(false);
  const [preflight, setPreflight] = useState<PreflightReport | null>(null);
  const [settingsTab, setSettingsTab] = useState<SettingsTabId>("page");
  const [previewTab, setPreviewTab] = useState<PreviewTabId>("spread");
  const [wizardOpen, setWizardOpen] = useState(false);

  const persona = useMemo(
    () => personaFromBookMetadata(book.metadata as Record<string, unknown>),
    [book.metadata]
  );
  const packagingVisible = showPublisherPackaging(persona) || persona === "author" || persona === "editor";

  const { coverLayout, persistCoverLayout: persistCover } = useCoverLayout(
    book,
    settings,
    previewPages.length > 0 ? previewPages.length : undefined
  );

  const localPreflight = useMemo(() => {
    return runPreflight({
      title: book.title,
      author: book.author,
      chapters: book.chapters,
      metadata: book.metadata as Record<string, unknown>,
      pageCount: coverLayout.pageCount,
      hasCoverArt: Boolean(coverLayout.image?.src),
      profile: "package",
      platform: settings.platform,
    });
  }, [book, coverLayout, settings.platform]);

  const publishReadiness = useMemo(() => assessPublishReadiness(book), [book]);

  useEffect(() => {
    setPreflight(localPreflight);
  }, [localPreflight]);

  function persistCoverLayout(next: BookCoverLayout) {
    persistCover(next, onBookChange);
  }

  async function handleCoverExport() {
    setCoverExporting(true);
    setExportMessage(null);
    try {
      const api = window.desktopV2Api;
      if (!api.exportCoverWrap) {
        setExportMessage({ text: "Cover export unavailable — rebuild the app.", type: "error" });
        return;
      }
      const result = await api.exportCoverWrap({ book, layout: coverLayout, authToken });
      if (result.canceled) setExportMessage(null);
      else if (result.upgrade) {
        setExportMessage({
          text: result.error || "Cover wrap requires Publish Pro (included with Editor).",
          type: "error",
        });
      } else if (result.error) setExportMessage({ text: userFacingError(result.error, "generic"), type: "error" });
      else if (result.wrapPath) {
        setExportMessage({
          text: `Cover wrap saved: ${result.wrapPath}${result.frontPath ? ` · front: ${result.frontPath}` : ""}`,
          type: "ok",
        });
      }
    } catch (e) {
      setExportMessage({ text: userFacingError(e, "generic"), type: "error" });
    } finally {
      setCoverExporting(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        const api = window.desktopV2Api;
        if (!api.paginatePublish) return;
        setPreviewBusy(true);
        try {
          const result = await api.paginatePublish({ book, settings });
          if (!cancelled && !result.error) setPreviewPages(result.pages as PageModel[]);
        } catch {
          if (!cancelled) setPreviewPages([]);
        } finally {
          if (!cancelled) setPreviewBusy(false);
        }
      })();
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [book, settings]);

  const update = useCallback(<K extends keyof PublishSettings>(key: K, value: PublishSettings[K]) => {
    setSettings((prev) => {
      const next = { ...prev, [key]: value };
      persistSettingsPatch(next, onBookChange);
      return next;
    });
  }, [onBookChange]);

  const updateTypography = useCallback(<K extends keyof PublishSettings["typography"]>(
    key: K,
    value: PublishSettings["typography"][K]
  ) => {
    setSettings((prev) => {
      const next = { ...prev, typography: { ...prev.typography, [key]: value } };
      persistSettingsPatch(next, onBookChange);
      return next;
    });
  }, [onBookChange]);

  const updateChapter = useCallback(<K extends keyof PublishSettings["chapterSettings"]>(
    key: K,
    value: PublishSettings["chapterSettings"][K]
  ) => {
    setSettings((prev) => {
      const next = { ...prev, chapterSettings: { ...prev.chapterSettings, [key]: value } };
      persistSettingsPatch(next, onBookChange);
      return next;
    });
  }, [onBookChange]);

  const updateDecoration = useCallback(
    (patch: Partial<ChapterDecoration>) => {
      setSettings((prev) => {
        const decoration = {
          ...(prev.chapterSettings.decoration ?? defaultChapterDecoration()),
          ...patch,
        };
        const next = {
          ...prev,
          chapterSettings: { ...prev.chapterSettings, decoration },
        };
        persistSettingsPatch(next, onBookChange);
        return next;
      });
    },
    [onBookChange]
  );

  const decorationFileRef = useRef<HTMLInputElement>(null);

  const onDecorationFile = useCallback(
    async (file: File | null) => {
      if (!file) return;
      try {
        const { dataUrl, aspect } = await fileToDecorationImage(file);
        updateDecoration({
          kind: "image",
          imageDataUrl: dataUrl,
          imageAspect: aspect,
        });
      } catch {
        // Ignore unreadable files; user can retry.
      }
    },
    [updateDecoration]
  );

  const updateToc = useCallback(<K extends keyof PublishSettings["toc"]>(
    key: K,
    value: PublishSettings["toc"][K]
  ) => {
    setSettings((prev) => {
      const next = { ...prev, toc: { ...prev.toc, [key]: value } };
      persistSettingsPatch(next, onBookChange);
      return next;
    });
  }, [onBookChange]);

  const updateHF = useCallback(<K extends keyof PublishSettings["headerFooter"]>(
    key: K,
    value: PublishSettings["headerFooter"][K]
  ) => {
    setSettings((prev) => {
      const next = { ...prev, headerFooter: { ...prev.headerFooter, [key]: value } };
      persistSettingsPatch(next, onBookChange);
      return next;
    });
  }, [onBookChange]);

  const updateFront = useCallback(<K extends keyof PublishSettings["frontMatter"]>(
    key: K,
    value: PublishSettings["frontMatter"][K]
  ) => {
    setSettings((prev) => {
      const next = { ...prev, frontMatter: { ...prev.frontMatter, [key]: value } };
      persistSettingsPatch(next, onBookChange);
      return next;
    });
  }, [onBookChange]);

  const updateBack = useCallback(<K extends keyof PublishSettings["backMatter"]>(
    key: K,
    value: PublishSettings["backMatter"][K]
  ) => {
    setSettings((prev) => {
      const next = { ...prev, backMatter: { ...prev.backMatter, [key]: value } };
      persistSettingsPatch(next, onBookChange);
      return next;
    });
  }, [onBookChange]);

  const applyPreset = (platform: Exclude<PublishPlatform, "custom">) => {
    const preset = PLATFORM_PRESETS[platform];
    setSettings(preset);
    persistSettingsPatch(preset, onBookChange);
  };

  const applyTheme = (themeId: string) => {
    setSettings((prev) => {
      const next = applyPublishTheme(prev, themeId);
      persistSettingsPatch(next, onBookChange);
      return next;
    });
  };

  const applySettingsFromWizard = (next: PublishSettings) => {
    setSettings(next);
    persistSettingsPatch(next, onBookChange);
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    setExportMessage(null);
    try {
      const api = window.desktopV2Api;
      if (!api.exportPublishPdf) {
        setExportMessage({ text: "Export not available — please rebuild the app.", type: "error" });
        return;
      }
      const result = await api.exportPublishPdf({ book, settings, authToken });
      if (result.canceled) {
        setExportMessage(null);
      } else if (result.upgrade) {
        setExportMessage({
          text: "Publish Formatter requires an Author or Editor plan. Open Settings → Upgrade to subscribe.",
          type: "error",
        });
      } else if (result.error) {
        setExportMessage({ text: userFacingError(result.error, "generic"), type: "error" });
      } else if (result.filePath) {
        setExportMessage({ text: `Saved: ${result.filePath}`, type: "ok" });
      }
    } catch (e) {
      setExportMessage({ text: userFacingError(e, "generic"), type: "error" });
    } finally {
      setExporting(false);
    }
  };

  const handleExportEpub = async () => {
    if (exporting) return;
    setExporting(true);
    setExportMessage(null);
    try {
      const api = window.desktopV2Api;
      if (!api.exportEpub) {
        setExportMessage({ text: "EPUB export not available — please rebuild the app.", type: "error" });
        return;
      }
      const result = await api.exportEpub({ book, authToken });
      if (result.canceled) {
        setExportMessage(null);
      } else if (result.upgrade) {
        setExportMessage({
          text: "EPUB export requires an Author or Editor plan. Open Settings → Upgrade to subscribe.",
          type: "error",
        });
      } else if (result.error) {
        setExportMessage({ text: userFacingError(result.error, "generic"), type: "error" });
      } else if (result.filePath) {
        setExportMessage({ text: `EPUB saved: ${result.filePath}`, type: "ok" });
      }
    } catch (e) {
      setExportMessage({ text: userFacingError(e, "generic"), type: "error" });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPackage = async () => {
    if (exporting) return;
    if (preflight && !preflight.ok) {
      setExportMessage({
        text: `Fix ${preflight.errorCount} blocking preflight issue${preflight.errorCount === 1 ? "" : "s"} before packaging.`,
        type: "error",
      });
      return;
    }
    setExporting(true);
    setExportMessage(null);
    try {
      const api = window.desktopV2Api;
      if (!api.exportPublishPackage) {
        setExportMessage({ text: "Package export not available — please rebuild the app.", type: "error" });
        return;
      }
      const result = await api.exportPublishPackage({
        book,
        settings,
        coverLayout,
        authToken,
      });
      if (result.report) setPreflight(result.report);
      if (result.canceled) {
        setExportMessage(null);
      } else if (result.upgrade) {
        setExportMessage({
          text: "Publisher package requires an Author or Editor plan. Open Settings → Upgrade to subscribe.",
          type: "error",
        });
      } else if (result.error) {
        setExportMessage({ text: userFacingError(result.error, "generic"), type: "error" });
      } else if (result.filePath) {
        setExportMessage({ text: `Package saved: ${result.filePath}`, type: "ok" });
      }
    } catch (e) {
      setExportMessage({ text: userFacingError(e, "generic"), type: "error" });
    } finally {
      setExporting(false);
    }
  };

  const estimatedPages = previewPages.length > 0 ? previewPages.length : estimateTotalPages(book, settings);
  const totalWords = book.chapters
    ?.reduce((sum, ch) => sum + (ch.content ? ch.content.trim().split(/\s+/).filter(Boolean).length : 0), 0) ?? 0;

  const trimOptions: { value: TrimSize; label: string }[] = Object.entries(TRIM_SIZE_LABELS).map(
    ([v, label]) => ({ value: v as TrimSize, label })
  );

  const fontOptions: { value: BodyFont; label: string }[] = Object.entries(BODY_FONT_LABELS).map(
    ([v, label]) => ({ value: v as BodyFont, label })
  );

  const publishFontOptions: { value: PublishFont | "same"; label: string }[] = [
    { value: "same", label: "Same as body" },
    ...Object.entries(PUBLISH_FONT_LABELS).map(([v, label]) => ({
      value: v as PublishFont,
      label,
    })),
  ];

  const headingSizeOptions = [12, 14, 16, 18, 20, 22, 24, 28, 32, 36].map((n) => ({
    value: String(n),
    label: `${n} pt`,
  }));

  const headingStyle = settings.chapterSettings.headingStyle;
  const showNumberSize =
    headingStyle === "number-only" || headingStyle === "number-and-title";
  const showTitleSize =
    headingStyle === "title-only" || headingStyle === "number-and-title";
  const decorationKind = settings.chapterSettings.decoration?.kind ?? "text";
  const showOrnamentFont = decorationKind === "text" || decorationKind === "ornament";


  return (
    <section className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden" aria-label="Publish formatter">
      <Card className="m-1 flex h-full min-h-0 flex-1 flex-col gap-0 overflow-hidden rounded-lg border border-border bg-card py-0 text-foreground shadow-sm ring-0">
        <PanelHeader
          title={persona === "writer" ? "Ready to publish?" : "Publish Formatter"}
          description={
            persona === "writer"
              ? "Export a simple PDF when you’re ready — or switch mode for covers, EPUB, and store packaging."
              : "Trim, typography, and export settings for print-ready PDF"
          }
          actions={
            <>
              {onRequestTranslate ? (
                <Button type="button" variant="secondary" size="sm" onClick={onRequestTranslate}>
                  Translate to language…
                </Button>
              ) : null}
              <Button type="button" variant="outline" size="sm" onClick={() => setWizardOpen(true)}>
                Publish wizard
              </Button>
            </>
          }
        />

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex min-h-0 flex-1 items-stretch">
            <aside className="flex min-h-0 w-1/3 min-w-64 max-w-1/3 flex-none flex-col border-r border-border bg-muted/15" aria-label="Format settings">
              <TabBar
                tabs={SETTINGS_TABS}
                value={settingsTab}
                onChange={setSettingsTab}
                ariaLabel="Settings sections"
                panelId="publish-settings-panel"
              />
              <div
                id="publish-settings-panel"
                className="min-h-0 flex-1 overflow-y-auto px-4 pt-3 pb-5"
                role="tabpanel"
                aria-labelledby={`publish-settings-panel-tab-${settingsTab}`}
              >
                {settingsTab === "page" && (
                  <>
                    <Section title="Platform">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant={settings.platform === "kdp-print" ? "secondary" : "outline"}
                          className="h-auto min-w-0 flex-1 basis-22 flex-col whitespace-normal py-2 text-center text-[0.8rem] leading-snug"
                          aria-pressed={settings.platform === "kdp-print"}
                          onClick={() => applyPreset("kdp-print")}
                        >
                          KDP Print
                          <span className="block text-[0.72rem] font-normal text-muted-foreground">6" × 9"</span>
                        </Button>
                        <Button
                          type="button"
                          variant={settings.platform === "ingramspark" ? "secondary" : "outline"}
                          className="h-auto min-w-0 flex-1 basis-22 flex-col whitespace-normal py-2 text-center text-[0.8rem] leading-snug"
                          aria-pressed={settings.platform === "ingramspark"}
                          onClick={() => applyPreset("ingramspark")}
                        >
                          IngramSpark
                          <span className="block text-[0.72rem] font-normal text-muted-foreground">6" × 9"</span>
                        </Button>
                        <Button
                          type="button"
                          variant={settings.platform === "custom" ? "secondary" : "outline"}
                          className="h-auto min-w-0 flex-1 basis-22 flex-col whitespace-normal py-2 text-center text-[0.8rem] leading-snug"
                          aria-pressed={settings.platform === "custom"}
                          onClick={() => update("platform", "custom")}
                        >
                          Custom
                          <span className="block text-[0.72rem] font-normal text-muted-foreground">Configure below</span>
                        </Button>
                      </div>
                      <p className="text-xs leading-snug text-muted-foreground">
                        Selecting a preset fills all formatting fields for that platform.
                      </p>
                    </Section>
                    <Section title="Page Size & Margins">
                      <div className="flex flex-col items-start gap-3">
                        <div className="flex flex-1 flex-col gap-2">
                          <Row label="Trim size">
                            <Select<TrimSize>
                              value={settings.trimSize}
                              onChange={(v) => { update("trimSize", v); update("platform", "custom"); }}
                              options={trimOptions}
                            />
                          </Row>
                          <Row label="Top">
                            <NumberInput value={settings.margins.top} min={0.25} max={2} onChange={(v) => update("margins", { ...settings.margins, top: v })} />
                            <span className="shrink-0 text-xs text-muted-foreground">in</span>
                          </Row>
                          <Row label="Bottom">
                            <NumberInput value={settings.margins.bottom} min={0.25} max={2} onChange={(v) => update("margins", { ...settings.margins, bottom: v })} />
                            <span className="shrink-0 text-xs text-muted-foreground">in</span>
                          </Row>
                          <Row label="Inner (gutter)">
                            <NumberInput value={settings.margins.inner} min={0.5} max={2} onChange={(v) => update("margins", { ...settings.margins, inner: v })} />
                            <span className="shrink-0 text-xs text-muted-foreground">in</span>
                          </Row>
                          <Row label="Outer">
                            <NumberInput value={settings.margins.outer} min={0.25} max={2} onChange={(v) => update("margins", { ...settings.margins, outer: v })} />
                            <span className="shrink-0 text-xs text-muted-foreground">in</span>
                          </Row>
                        </div>
                        <PageDiagram settings={settings} />
                      </div>
                    </Section>
                  </>
                )}

                {settingsTab === "themes" && (
                  <Section title="Themes" defaultOpen>
                    <PublishThemeStrip onApplyTheme={applyTheme} />
                    <p className="mt-3 text-xs leading-snug text-muted-foreground">
                      Or run the guided{" "}
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => setWizardOpen(true)}
                      >
                        Publish wizard
                      </Button>{" "}
                      (theme → trim → preflight → export).
                    </p>
                  </Section>
                )}

                {settingsTab === "type" && (
                  <>
                    <Section title="Typography">
                      <Row label="Body font">
                        <Select<BodyFont>
                          value={settings.typography.bodyFont}
                          onChange={(v) => updateTypography("bodyFont", v)}
                          options={fontOptions}
                        />
                      </Row>
                      <p className="text-xs leading-snug text-muted-foreground">
                        Body fonts are embedded from assets/fonts (EB Garamond, Crimson Text, Libre
                        Baskerville, Liberation Serif). Chapter headings may also use Playfair Display or
                        Cinzel. Preview and PDF share the same paginator.
                      </p>
                      <Row label="Font size">
                        <Select
                          value={String(settings.typography.bodyFontSize)}
                          onChange={(v) => updateTypography("bodyFontSize", parseInt(v, 10))}
                          options={[10, 11, 12, 13, 14].map((n) => ({ value: String(n), label: `${n} pt` }))}
                        />
                      </Row>
                      <Row label="Line spacing">
                        <Select
                          value={String(settings.typography.lineSpacing)}
                          onChange={(v) => updateTypography("lineSpacing", parseFloat(v))}
                          options={LINE_SPACING_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))}
                        />
                      </Row>
                      <Row label="Paragraphs">
                        <Button
                          type="button"
                          size="sm"
                          variant={settings.typography.paragraphStyle === "indent" ? "secondary" : "outline"}
                          aria-pressed={settings.typography.paragraphStyle === "indent"}
                          onClick={() => updateTypography("paragraphStyle", "indent")}
                        >
                          First-line indent
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={settings.typography.paragraphStyle === "spacing" ? "secondary" : "outline"}
                          aria-pressed={settings.typography.paragraphStyle === "spacing"}
                          onClick={() => {
                            setSettings((prev) => {
                              const next = {
                                ...prev,
                                typography: {
                                  ...prev.typography,
                                  paragraphStyle: "spacing" as const,
                                  paragraphSpacing:
                                    prev.typography.paragraphSpacing > 0
                                      ? prev.typography.paragraphSpacing
                                      : 6,
                                },
                              };
                              persistSettingsPatch(next, onBookChange);
                              return next;
                            });
                          }}
                        >
                          Block spacing
                        </Button>
                      </Row>
                      {settings.typography.paragraphStyle === "indent" && (
                        <Row label="Indent depth">
                          <Select
                            value={String(settings.typography.firstLineIndent)}
                            onChange={(v) => updateTypography("firstLineIndent", parseInt(v, 10))}
                            options={[12, 18, 24, 30, 36].map((n) => ({ value: String(n), label: `${n} pt` }))}
                          />
                        </Row>
                      )}
                      <Row label="Space between paragraphs">
                        <Select
                          value={String(settings.typography.paragraphSpacing ?? 0)}
                          onChange={(v) => updateTypography("paragraphSpacing", parseInt(v, 10))}
                          options={[0, 3, 4, 6, 8, 9, 12, 18].map((n) => ({
                            value: String(n),
                            label: n === 0 ? "None" : `${n} pt`,
                          }))}
                        />
                      </Row>
                    </Section>
                    <Section title="Section Break">
                      <Row label="Ornament">
                        <Input
                          type="text"
                          value={settings.sectionBreakOrnament}
                          onChange={(e) => update("sectionBreakOrnament", e.target.value)}
                          placeholder="* * *"
                        />
                      </Row>
                      <div className="rounded-lg border border-dashed border-border p-2 text-center text-sm italic text-muted-foreground">
                        <span>{settings.sectionBreakOrnament || "* * *"}</span>
                      </div>
                    </Section>
                  </>
                )}

                {settingsTab === "chapters" && (
                  <>
                    <Section title="Chapter Settings">
                      <Row label="Heading style">
                        <Select<ChapterHeadingStyle>
                          value={settings.chapterSettings.headingStyle}
                          onChange={(v) => updateChapter("headingStyle", v)}
                          options={[
                            { value: "number-and-title", label: "Chapter 1: Title" },
                            { value: "title-only", label: "Title Only" },
                            { value: "number-only", label: "Chapter Number Only" },
                          ]}
                        />
                      </Row>
                      <Row label="Number format">
                        <Select<ChapterNumberFormat>
                          value={settings.chapterSettings.numberFormat}
                          onChange={(v) => updateChapter("numberFormat", v)}
                          options={[
                            { value: "arabic", label: "Arabic (Chapter 1)" },
                            { value: "roman", label: "Roman (Chapter I)" },
                            { value: "written", label: "Written (Chapter One)" },
                          ]}
                        />
                      </Row>
                      <Row label="Chapter start">
                        <Select<ChapterStartPage>
                          value={settings.chapterSettings.chapterStartPage}
                          onChange={(v) => updateChapter("chapterStartPage", v)}
                          options={[
                            { value: "any", label: "Any page" },
                            { value: "recto", label: "Always right-hand page" },
                          ]}
                        />
                      </Row>
                      <Row label="Drop cap">
                        <Toggle
                          value={settings.chapterSettings.dropCap}
                          onChange={(v) => updateChapter("dropCap", v)}
                          label="First letter enlarged"
                        />
                      </Row>
                      <Row label="Heading align">
                        <Select<HeadingAlign>
                          value={settings.chapterSettings.headingAlign ?? "center"}
                          onChange={(v) => updateChapter("headingAlign", v)}
                          options={[
                            { value: "left", label: "Left" },
                            { value: "center", label: "Center" },
                            { value: "right", label: "Right" },
                          ]}
                        />
                      </Row>
                      <Row label="Heading font">
                        <Select<PublishFont | "same">
                          value={settings.chapterSettings.headingFont ?? "same"}
                          onChange={(v) => updateChapter("headingFont", v)}
                          options={publishFontOptions}
                        />
                      </Row>
                      {showNumberSize ? (
                        <Row label="Number size">
                          <Select
                            value={String(
                              settings.chapterSettings.numberFontSize ??
                                settings.chapterSettings.headingFontSize ??
                                22
                            )}
                            onChange={(v) => {
                              const n = parseInt(v, 10);
                              setSettings((prev) => {
                                const next = {
                                  ...prev,
                                  chapterSettings: {
                                    ...prev.chapterSettings,
                                    numberFontSize: n,
                                    headingFontSize: n,
                                  },
                                };
                                persistSettingsPatch(next, onBookChange);
                                return next;
                              });
                            }}
                            options={headingSizeOptions}
                          />
                        </Row>
                      ) : null}
                      {showTitleSize ? (
                        <Row label="Title size">
                          <Select
                            value={String(settings.chapterSettings.titleFontSize ?? 16)}
                            onChange={(v) => updateChapter("titleFontSize", parseInt(v, 10))}
                            options={headingSizeOptions}
                          />
                        </Row>
                      ) : null}
                      <Row label="Space before heading">
                        <Select
                          value={String(settings.chapterSettings.spaceBefore)}
                          onChange={(v) => updateChapter("spaceBefore", parseInt(v, 10))}
                          options={[
                            { value: "36", label: "0.5 in" },
                            { value: "54", label: "0.75 in" },
                            { value: "72", label: "1 in" },
                            { value: "108", label: "1.5 in" },
                            { value: "144", label: "2 in" },
                          ]}
                        />
                      </Row>
                      <Row label="Space after heading">
                        <Select
                          value={String(settings.chapterSettings.spaceAfter ?? 20)}
                          onChange={(v) => updateChapter("spaceAfter", parseInt(v, 10))}
                          options={[
                            { value: "20", label: "20 pt" },
                            { value: "36", label: "0.5 in" },
                            { value: "54", label: "0.75 in" },
                            { value: "72", label: "1 in" },
                            { value: "108", label: "1.5 in" },
                            { value: "144", label: "2 in" },
                          ]}
                        />
                      </Row>
                      <Row label="Decoration">
                        <Toggle
                          value={Boolean(settings.chapterSettings.showDecoration)}
                          onChange={(v) => updateChapter("showDecoration", v)}
                          label="Show under heading"
                        />
                      </Row>
                      {settings.chapterSettings.showDecoration ? (
                        <>
                          <Row label="Decoration source">
                            <Select<ChapterDecorationKind>
                              value={decorationKind}
                              onChange={(v) => updateDecoration({ kind: v })}
                              options={[
                                { value: "text", label: "Text" },
                                { value: "ornament", label: "Ornament" },
                                { value: "line", label: "Line" },
                                { value: "image", label: "Image" },
                              ]}
                            />
                          </Row>
                          {decorationKind === "text" ? (
                            <Row label="Decoration text">
                              <Input
                                type="text"
                                value={
                                  settings.chapterSettings.decoration?.text ?? "* * *"
                                }
                                onChange={(e) =>
                                  updateDecoration({ text: e.target.value })
                                }
                                placeholder="* * *"
                              />
                            </Row>
                          ) : null}
                          {decorationKind === "ornament" ? (
                            <Row label="Ornament">
                              <Select
                                value={
                                  settings.chapterSettings.decoration?.ornament ??
                                  "* * *"
                                }
                                onChange={(v) => updateDecoration({ ornament: v })}
                                options={CHAPTER_DECORATION_ORNAMENTS}
                              />
                            </Row>
                          ) : null}
                          {decorationKind === "line" ? (
                            <Row label="Line style">
                              <Select<ChapterDecorationLineStyle>
                                value={
                                  settings.chapterSettings.decoration?.lineStyle ?? "thin"
                                }
                                onChange={(v) => updateDecoration({ lineStyle: v })}
                                options={CHAPTER_DECORATION_LINE_STYLES}
                              />
                            </Row>
                          ) : null}
                          {showOrnamentFont ? (
                            <Row label="Ornament font">
                              <Select<PublishFont | "same">
                                value={settings.chapterSettings.decorationFont ?? "same"}
                                onChange={(v) => updateChapter("decorationFont", v)}
                                options={[
                                  { value: "same", label: "Same as heading" },
                                  ...Object.entries(PUBLISH_FONT_LABELS).map(([v, label]) => ({
                                    value: v as PublishFont,
                                    label,
                                  })),
                                ]}
                              />
                            </Row>
                          ) : null}
                          {decorationKind === "image" ? (
                            <Row label="Decoration image">
                              <div className="flex flex-wrap items-center gap-2">
                                {settings.chapterSettings.decoration?.imageDataUrl ? (
                                  <img
                                    className="h-7 w-10 rounded-lg border border-border bg-white object-contain"
                                    src={settings.chapterSettings.decoration.imageDataUrl}
                                    alt="Chapter decoration"
                                  />
                                ) : null}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => decorationFileRef.current?.click()}
                                >
                                  {settings.chapterSettings.decoration?.imageDataUrl
                                    ? "Replace…"
                                    : "Upload…"}
                                </Button>
                                {settings.chapterSettings.decoration?.imageDataUrl ? (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      updateDecoration({
                                        imageDataUrl: null,
                                        imageAspect: null,
                                      })
                                    }
                                  >
                                    Clear
                                  </Button>
                                ) : null}
                                <Input
                                  ref={decorationFileRef}
                                  type="file"
                                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                  className="hidden"
                                  onChange={(e) => {
                                    void onDecorationFile(e.target.files?.[0] ?? null);
                                    e.target.value = "";
                                  }}
                                />
                              </div>
                            </Row>
                          ) : null}
                          <div className="rounded-lg border border-dashed border-border p-2 text-center text-sm italic text-muted-foreground">
                            {decorationKind === "image" ? (
                              settings.chapterSettings.decoration?.imageDataUrl ? (
                                <img
                                  className="inline-block max-h-9 max-w-full object-contain align-middle"
                                  src={settings.chapterSettings.decoration.imageDataUrl}
                                  alt=""
                                />
                              ) : (
                                <span className="text-xs leading-snug text-muted-foreground">No image uploaded</span>
                              )
                            ) : decorationKind === "line" ? (
                              <span className="text-xs leading-snug text-muted-foreground">
                                {settings.chapterSettings.decoration?.lineStyle ?? "thin"}{" "}
                                rule
                              </span>
                            ) : (
                              <span>
                                {decorationKind === "ornament"
                                  ? settings.chapterSettings.decoration?.ornament ||
                                    "* * *"
                                  : settings.chapterSettings.decoration?.text || "* * *"}
                              </span>
                            )}
                          </div>
                        </>
                      ) : null}
                    </Section>
                    <Section title="Chapter caption">
                      <p className="text-xs leading-snug text-muted-foreground">
                        Captions are set per chapter in the manuscript header. These options style them in
                        print preview and export.
                      </p>
                      <Row label="Show captions">
                        <Toggle
                          value={settings.chapterSettings.showCaption !== false}
                          onChange={(v) => updateChapter("showCaption", v)}
                          label="Render chapter captions"
                        />
                      </Row>
                      <Row label="Placement">
                        <Select<CaptionPlacement>
                          value={settings.chapterSettings.captionPlacement ?? "below-title"}
                          onChange={(v) => updateChapter("captionPlacement", v)}
                          options={[
                            { value: "below-title", label: "Below title" },
                            { value: "above-title", label: "Above title" },
                          ]}
                        />
                      </Row>
                      <Row label="Alignment">
                        <Select<CaptionAlign>
                          value={settings.chapterSettings.captionAlign ?? "center"}
                          onChange={(v) => updateChapter("captionAlign", v)}
                          options={[
                            { value: "center", label: "Center" },
                            { value: "left", label: "Left" },
                          ]}
                        />
                      </Row>
                      <Row label="Italic">
                        <Toggle
                          value={settings.chapterSettings.captionItalic !== false}
                          onChange={(v) => updateChapter("captionItalic", v)}
                          label="Italic caption"
                        />
                      </Row>
                      <Row label="Font size">
                        <Select
                          value={String(settings.chapterSettings.captionFontSize ?? 11)}
                          onChange={(v) => updateChapter("captionFontSize", parseInt(v, 10))}
                          options={[9, 10, 11, 12, 13, 14].map((n) => ({
                            value: String(n),
                            label: `${n} pt`,
                          }))}
                        />
                      </Row>
                      <Row label="Space before">
                        <Select
                          value={String(settings.chapterSettings.captionSpaceBefore ?? 8)}
                          onChange={(v) => updateChapter("captionSpaceBefore", parseInt(v, 10))}
                          options={[0, 4, 6, 8, 12, 16, 20].map((n) => ({
                            value: String(n),
                            label: `${n} pt`,
                          }))}
                        />
                      </Row>
                      <Row label="Space after">
                        <Select
                          value={String(settings.chapterSettings.captionSpaceAfter ?? 16)}
                          onChange={(v) => updateChapter("captionSpaceAfter", parseInt(v, 10))}
                          options={[0, 8, 12, 16, 20, 24, 32].map((n) => ({
                            value: String(n),
                            label: `${n} pt`,
                          }))}
                        />
                      </Row>
                    </Section>
                    <Section title="Table of Contents">
                      <Row label="Include TOC">
                        <Toggle value={settings.toc.include} onChange={(v) => updateToc("include", v)} />
                      </Row>
                      {settings.toc.include && (
                        <>
                          <Row label="TOC heading">
                            <Input
                              type="text"
                              value={settings.toc.title}
                              onChange={(e) => updateToc("title", e.target.value)}
                              placeholder="Contents"
                            />
                          </Row>
                          <Row label="TOC style">
                            <Select<TocStyle>
                              value={settings.toc.style}
                              onChange={(v) => updateToc("style", v)}
                              options={[
                                { value: "dot-leaders", label: "Dot Leaders (traditional)" },
                                { value: "modern", label: "Modern (no dots)" },
                                { value: "minimal", label: "Minimal (title only)" },
                              ]}
                            />
                          </Row>
                        </>
                      )}
                    </Section>
                  </>
                )}

                {settingsTab === "matter" && (
                  <>
                    <Section title="Front Matter">
                      <Row label="Half-title page">
                        <Toggle value={settings.frontMatter.halfTitle} onChange={(v) => updateFront("halfTitle", v)} />
                      </Row>
                      <Row label="Title page">
                        <Toggle value={settings.frontMatter.titlePage} onChange={(v) => updateFront("titlePage", v)} />
                      </Row>
                      <Row label="Copyright page">
                        <Toggle value={settings.frontMatter.copyrightPage} onChange={(v) => updateFront("copyrightPage", v)} />
                      </Row>
                      {settings.frontMatter.copyrightPage && (
                        <>
                          <Row label="Copyright year">
                            <Input
                              type="text"
                              className="max-w-20 flex-none"
                              value={settings.frontMatter.copyrightYear}
                              onChange={(e) => updateFront("copyrightYear", e.target.value)}
                              maxLength={4}
                            />
                          </Row>
                          <Row label="ISBN (optional)">
                            <Input
                              type="text"
                              value={settings.frontMatter.isbn}
                              onChange={(e) => updateFront("isbn", e.target.value)}
                              placeholder="978-0-000-00000-0"
                            />
                          </Row>
                        </>
                      )}
                      <Row label="Dedication page">
                        <Toggle value={settings.frontMatter.dedicationPage} onChange={(v) => updateFront("dedicationPage", v)} />
                      </Row>
                      {settings.frontMatter.dedicationPage && (
                        <Textarea
                          value={settings.frontMatter.dedicationText}
                          onChange={(e) => updateFront("dedicationText", e.target.value)}
                          placeholder="For…"
                          rows={3}
                        />
                      )}
                    </Section>
                    <Section title="Back Matter">
                      <Row label="About the Author">
                        <Toggle value={settings.backMatter.aboutAuthor} onChange={(v) => updateBack("aboutAuthor", v)} />
                      </Row>
                      {settings.backMatter.aboutAuthor && (
                        <Textarea
                          value={settings.backMatter.aboutAuthorText}
                          onChange={(e) => updateBack("aboutAuthorText", e.target.value)}
                          placeholder="Author biography…"
                          rows={4}
                        />
                      )}
                      <Row label="Also by This Author">
                        <Toggle value={settings.backMatter.alsoBy} onChange={(v) => updateBack("alsoBy", v)} />
                      </Row>
                      {settings.backMatter.alsoBy && (
                        <Textarea
                          value={settings.backMatter.alsoByText}
                          onChange={(e) => updateBack("alsoByText", e.target.value)}
                          placeholder="List other titles…"
                          rows={3}
                        />
                      )}
                      <Row label="Blank page at end">
                        <Toggle value={settings.backMatter.blankPageAtEnd} onChange={(v) => updateBack("blankPageAtEnd", v)} />
                      </Row>
                    </Section>
                  </>
                )}

                {settingsTab === "headers" && (
                  <Section title="Headers & Page Numbers">
                    <Row label="Running header">
                      <Select<HeaderContent>
                        value={settings.headerFooter.headerContent}
                        onChange={(v) => updateHF("headerContent", v)}
                        options={[
                          { value: "none", label: "None" },
                          { value: "book-title", label: "Book Title" },
                          { value: "author-name", label: "Author Name" },
                          { value: "chapter-title", label: "Current Chapter" },
                          { value: "alternating", label: "Alternating (author / title)" },
                        ]}
                      />
                    </Row>
                    <Row label="Page numbers">
                      <Select<PageNumberPlacement>
                        value={settings.headerFooter.pageNumbers}
                        onChange={(v) => updateHF("pageNumbers", v)}
                        options={[
                          { value: "none", label: "None" },
                          { value: "bottom-center", label: "Bottom Center" },
                          { value: "bottom-outer", label: "Bottom Outer" },
                          { value: "bottom-inner", label: "Bottom Inner" },
                        ]}
                      />
                    </Row>
                    <Row label="Suppress on chapter starts">
                      <Toggle
                        value={settings.headerFooter.suppressOnChapterFirstPage}
                        onChange={(v) => updateHF("suppressOnChapterFirstPage", v)}
                        label="Hide header/footer on first page of each chapter"
                      />
                    </Row>
                  </Section>
                )}

                {settingsTab === "preflight" && (
                  <Section title="Preflight">
                    <p className="mb-2 text-xs leading-snug text-muted-foreground">
                      Preflight checks KDP/Ingram-oriented readiness before you export — fix blocking
                      issues first.
                    </p>
                    <p className="mb-2.5 text-sm text-muted-foreground">
                      {preflight?.ok
                        ? `Ready to package · ${preflight.warningCount} warning${preflight.warningCount === 1 ? "" : "s"}`
                        : `${preflight?.errorCount ?? 0} blocking · ${preflight?.warningCount ?? 0} warning${(preflight?.warningCount ?? 0) === 1 ? "" : "s"}`}
                    </p>
                    <ul className="flex list-none flex-col gap-2 p-0">
                      {(preflight?.issues ?? []).map((issue) => (
                        <li
                          key={`${issue.code}-${issue.message}`}
                          className="flex items-start gap-2 rounded-md border border-border bg-muted/25 px-2 py-1.5"
                        >
                          <span
                            className={cn(
                              "shrink-0 rounded px-1.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide",
                              issue.level === "error" && "bg-destructive/15 text-destructive",
                              issue.level === "warning" && "bg-amber-500/15 text-amber-700 dark:text-amber-300",
                              issue.level === "info" && "bg-muted text-muted-foreground"
                            )}
                          >
                            {issue.level}
                          </span>
                          <div>
                            <div className="text-sm text-foreground">{issue.message}</div>
                            {issue.fix ? <div className="mt-1 text-xs text-muted-foreground">{issue.fix}</div> : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}
              </div>
            </aside>

            <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-card" aria-label="Preview and layout">
              <TabBar
                tabs={PREVIEW_TABS}
                value={previewTab}
                onChange={setPreviewTab}
                ariaLabel="Preview views"
                panelId="publish-preview-panel"
              />
              <div
                id="publish-preview-panel"
                className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-muted/15 px-4 pt-3 pb-5"
                role="tabpanel"
                aria-labelledby={`publish-preview-panel-tab-${previewTab}`}
              >
                {previewTab === "spread" && (
                  previewBusy && previewPages.length === 0 ? (
                    <p className="text-xs leading-snug text-muted-foreground">Paginating with embedded fonts…</p>
                  ) : (
                    <WysiwygSpreadPreview pages={previewPages} scale={0.85} />
                  )
                )}
                {previewTab === "cover" && (
                  <CoverEditor
                    bookTitle={book.title || "Untitled"}
                    authorName={book.author || ""}
                    pageCount={coverLayout.pageCount}
                    trimSize={settings.trimSize}
                    platform={coverLayout.platform}
                    layout={coverLayout}
                    onChange={persistCoverLayout}
                    onExport={() => void handleCoverExport()}
                    exporting={coverExporting}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <footer className="shrink-0 border-t border-border bg-muted/20">
          <div className="flex flex-col gap-2.5 px-5 pt-3.5 pb-4">
            <div className="flex gap-1.5 text-[0.8rem] text-muted-foreground">
              <span>~{estimatedPages} pages</span>
              <span className="text-border">·</span>
              <span>{totalWords.toLocaleString()} words</span>
            </div>
            {exportMessage ? (
              <div
                className={cn(
                  "break-all rounded-lg border border-border px-2 py-1.5 text-[0.8rem] text-foreground",
                  exportMessage.type === "ok" ? "bg-primary/10" : "bg-destructive/10"
                )}
                role="status"
              >
                {exportMessage.text}
              </div>
            ) : null}
            <div className="flex flex-col gap-2">
              <AsyncButton
                type="button"
                className="w-full font-semibold"
                onClick={handleExport}
                busy={exporting}
                busyLabel="Exporting…"
              >
                Export Publication PDF
              </AsyncButton>
              <Button
                type="button"
                variant="outline"
                className="w-full font-semibold"
                onClick={handleExportEpub}
                disabled={exporting}
              >
                Export EPUB
              </Button>
              {packagingVisible ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full font-semibold"
                  onClick={handleExportPackage}
                  disabled={exporting || Boolean(preflight && !preflight.ok)}
                  title={
                    preflight && !preflight.ok
                      ? "Resolve blocking preflight issues first"
                      : "ZIP: interior PDF + cover + EPUB + metadata.json"
                  }
                >
                  Export Publisher Package
                </Button>
              ) : (
                <p className="text-xs leading-snug text-muted-foreground">
                  Upgrade to Author or Editor for the full store package (PDF + cover + EPUB).
                </p>
              )}
            </div>
          </div>
        </footer>
      </Card>
      <PublishWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        settings={settings}
        onApplySettings={applySettingsFromWizard}
        preflight={preflight}
        onExportPdf={() => void handleExport()}
        exporting={exporting}
        readiness={publishReadiness}
      />
    </section>
  );
}
